// The archive — everything that was deleted, and the two ways out of it.
//
// Deleting anywhere in the app now puts the thing here instead of destroying
// it. This is where it comes back from, and the only place it can actually be
// destroyed.
//
// Restore is a plain button. Destroy is not: it types the count of days it has
// been waiting into the confirm, and says what it takes with it, because it is
// the one action in stallSIDE with nothing behind it.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { fmtStamp } from '../../lib/dates';

export default function ArchivePage() {
  const { activeRole } = useAuth();
  const canManage = activeRole === 'owner' || activeRole === 'manager';
  const qc = useQueryClient();
  const [err, setErr] = useState('');
  const [busyId, setBusyId] = useState(null);

  const archiveQ = useQuery({
    queryKey: ['archive'],
    queryFn: () => api.get('/archive'),
    enabled: canManage,
    retry: false,
  });

  // Restoring a horse puts it back on the roster; restoring a lesson puts it
  // back on the schedule. Nothing here knows which, so everything refreshes.
  const refreshAll = () => {
    qc.invalidateQueries();
    setBusyId(null);
  };

  const restore = useMutation({
    mutationFn: (e) => api.post(`/archive/${e.type}/${e.id}/restore`),
    onSuccess: () => { setErr(''); refreshAll(); },
    onError: (e) => { setErr(friendly(e)); setBusyId(null); },
  });

  const destroy = useMutation({
    mutationFn: (e) => api.del(`/archive/${e.type}/${e.id}`),
    onSuccess: () => { setErr(''); refreshAll(); },
    onError: (e) => { setErr(friendly(e)); setBusyId(null); },
  });

  if (!canManage) {
    return (
      <div className="p-4 md:p-6 max-w-3xl mx-auto">
        <p className="text-ink-400 text-sm">Only barn owners and managers can see the archive.</p>
      </div>
    );
  }

  const entries = archiveQ.data?.entries ?? [];
  const purgeDays = archiveQ.data?.purgeAfterDays ?? 90;

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto space-y-4 md:space-y-6">
      <header>
        <h1 className="text-2xl md:text-3xl font-semibold">Archive</h1>
        <p className="text-ink-400 text-sm mt-1">
          Deleted horses, notes, records, photos and lessons. Restore anything from here.
          Items are destroyed for good after {purgeDays} days.
        </p>
      </header>

      {err && <div className="rounded-lg bg-rose-50 text-rose-700 text-sm px-3 py-2">{err}</div>}

      {archiveQ.isLoading ? (
        <div className="card p-4">
          <div className="h-4 w-40 bg-ink-100 rounded animate-pulse" />
        </div>
      ) : entries.length === 0 ? (
        <div className="card p-6 text-center">
          <p className="text-ink-500">Nothing has been deleted.</p>
        </div>
      ) : (
        <ul className="card divide-y divide-ink-100">
          {entries.map((e) => {
            const left = purgeDays - daysSince(e.archivedAt);
            const busy = busyId === `${e.type}:${e.id}`;
            return (
              <li key={`${e.type}:${e.id}`} className="p-4 flex items-start gap-3">
                <span className="min-w-0 flex-1">
                  <span className="block text-xs text-ink-400">
                    {e.kindLabel}
                    {e.horseName ? ` · ${e.horseName}` : ''}
                  </span>
                  <span className="block font-medium truncate">{e.label}</span>
                  <span className="block text-xs text-ink-400 mt-0.5">
                    Deleted {whenLabel(e.archivedAt)}
                    {' · '}
                    <span className={left <= 7 ? 'text-rose-600' : ''}>
                      {left <= 0 ? 'due to be destroyed' : `${left} days left`}
                    </span>
                  </span>
                </span>

                <span className="shrink-0 flex items-center gap-2">
                  <button
                    type="button"
                    className="btn-secondary text-sm"
                    disabled={busy}
                    onClick={() => { setBusyId(`${e.type}:${e.id}`); restore.mutate(e); }}
                  >
                    Restore
                  </button>
                  <button
                    type="button"
                    className="text-sm text-rose-600 hover:text-rose-800 px-2 py-2"
                    disabled={busy}
                    onClick={() => {
                      if (window.confirm(destroyWarning(e))) {
                        setBusyId(`${e.type}:${e.id}`);
                        destroy.mutate(e);
                      }
                    }}
                  >
                    Destroy
                  </button>
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Say what goes with it. Destroying a horse takes its whole record. */
function destroyWarning(e) {
  if (e.type === 'horse') {
    return (
      `Permanently destroy ${e.label}?\n\n` +
      'This also destroys every note, health record and photo belonging to this ' +
      'horse, and the image and document files themselves. It cannot be undone.'
    );
  }
  if (e.type === 'photo' || e.type === 'health_record') {
    return `Permanently destroy this ${e.kindLabel.toLowerCase()}, including its file? This cannot be undone.`;
  }
  return `Permanently destroy this ${e.kindLabel.toLowerCase()}? This cannot be undone.`;
}

function daysSince(unix) {
  return Math.floor((Date.now() / 1000 - unix) / 86400);
}

function whenLabel(unix) {
  const days = daysSince(unix);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  return fmtStamp(unix, 'medium');
}

function friendly(e) {
  switch (e?.body?.error || e?.message || '') {
    case 'not_found':
      return 'That is no longer in the archive — someone may have got there first.';
    case 'unknown_type':
      return 'That kind of item cannot be restored.';
    case 'forbidden':
      return 'Only barn owners and managers can do that.';
    default:
      return 'That did not go through. Try again.';
  }
}
